/**
 * Mi Boleta — useFlashSaleQueue · estado de la Fila Virtual (Flash Sales).
 *
 * Maneja el estado de la cola de una venta de alta demanda y su ciclo de vida:
 * entrada a la fila, **polling cada 5s** contra el motor real de `api-core`
 * (`/v1/flash-sales/{id}/queue/*`, migración 0087), transición a `ready` cuando
 * el backend asigna el turno, y **guarda `beforeunload`** mientras se espera.
 *
 * Antes esto simulaba el avance en local con `Math.random()`. Ahora la posición,
 * el ETA y el turno los decide el backend — que es el único que puede saberlos,
 * porque la fila es compartida entre todos los fans. **La superficie del hook no
 * cambió**: quien lo consume sigue recibiendo `usersAhead`/`estimatedSeconds`/
 * `status`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApiError } from '@/lib/api';
import {
  getFlashQueueStatus,
  joinFlashQueue,
  leaveFlashQueue,
  type FlashQueueStatusDto,
} from '@/lib/api/core';

export type QueueStatus = 'waiting' | 'ready';

export interface UseFlashSaleQueueOptions {
  /** Venta a la que se hace fila. Sin esto el hook queda inactivo. */
  flashSaleId?: string;
  /** Cadencia del polling, en ms. Default 5000 (5s). */
  pollMs?: number;
  /** Se dispara UNA vez cuando el backend asigna el turno. */
  onReady?: () => void;
  /** Advertencia nativa al cerrar/recargar mientras se espera. Default true. */
  guardUnload?: boolean;
}

export interface FlashSaleQueueState {
  usersAhead: number;
  estimatedSeconds: number;
  status: QueueStatus;
  /** Posición 1-based, para mostrarla sin recalcular. */
  position: number;
  /** Hasta cuándo vale el turno (ISO). `null` mientras se espera. */
  admittedUntil: string | null;
  /** El backend todavía no respondió la primera vez. */
  isLoading: boolean;
  /** Falló la comunicación con el motor de fila. */
  error: Error | null;
  /**
   * El motor rechazó por falta de sesión (401). Va aparte del `error` genérico
   * porque no es algo que mostrar: es una condición de RUTEO. Sin sesión no hay
   * fila posible, y la pantalla no debe fingir una espera que no existe.
   */
  isUnauthorized: boolean;
  /** Salir de la fila y liberar el lugar. */
  leave: () => Promise<void>;
}

/** Cadencia de polling — 5s, alineada con el resto del polling de commerce. */
export const QUEUE_POLL_MS = 5000;

export function useFlashSaleQueue({
  flashSaleId,
  pollMs = QUEUE_POLL_MS,
  onReady,
  guardUnload = true,
}: UseFlashSaleQueueOptions = {}): FlashSaleQueueState {
  const [snapshot, setSnapshot] = useState<FlashQueueStatusDto | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(flashSaleId));
  const [error, setError] = useState<Error | null>(null);
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  // `onReady` sin re-suscribir el efecto de polling.
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const readyFiredRef = useRef(false);

  const status: QueueStatus = snapshot?.status === 'ready' ? 'ready' : 'waiting';

  // Entrada + polling. Se une una vez y después sólo consulta: unirse es
  // idempotente en el backend, pero repetirlo sería ruido.
  useEffect(() => {
    if (!flashSaleId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    const apply = (data: FlashQueueStatusDto) => {
      if (cancelled) return;
      setSnapshot(data);
      setError(null);
      setIsLoading(false);
    };

    const fail = (err: unknown) => {
      if (cancelled) return;
      if (err instanceof ApiError && err.status === 401) setIsUnauthorized(true);
      setError(err instanceof Error ? err : new Error('Error de fila'));
      setIsLoading(false);
    };

    (async () => {
      try {
        apply(await joinFlashQueue(flashSaleId));
      } catch (err) {
        fail(err);
        return;
      }
      // Si ya entró con turno, no hace falta pollear.
      timer = setInterval(async () => {
        try {
          apply(await getFlashQueueStatus(flashSaleId));
        } catch (err) {
          // Un poll fallido no saca al fan de la fila: se reintenta al siguiente.
          fail(err);
        }
      }, pollMs);
    })();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [flashSaleId, pollMs]);

  // El polling se detiene solo al llegar el turno.
  useEffect(() => {
    if (status !== 'ready' || readyFiredRef.current) return;
    readyFiredRef.current = true;
    onReadyRef.current?.();
  }, [status]);

  // Protección de navegación: la advertencia sólo vive mientras se espera.
  // Al pasar a `ready` el efecto se limpia → la redirección de salida no la dispara.
  useEffect(() => {
    if (!guardUnload || status !== 'waiting' || !flashSaleId) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome exige setear returnValue para mostrar el diálogo nativo.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [guardUnload, status, flashSaleId]);

  const leave = useCallback(async () => {
    if (!flashSaleId) return;
    try {
      await leaveFlashQueue(flashSaleId);
    } catch {
      // Irse es best-effort: si falla, el turno vence solo y libera el cupo.
    }
  }, [flashSaleId]);

  return useMemo(
    () => ({
      usersAhead: snapshot?.users_ahead ?? 0,
      estimatedSeconds: snapshot?.estimated_seconds ?? 0,
      status,
      position: snapshot?.position ?? 1,
      admittedUntil: snapshot?.admitted_until ?? null,
      isUnauthorized,
      isLoading,
      error,
      leave,
    }),
    [snapshot, status, isLoading, error, isUnauthorized, leave],
  );
}
